import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-label", "UNSAFE_touchableProps", "ariaLabel", "backgroundColorToken", "backgroundColorTokenArgs", "borderColorToken", "borderColorTokenArgs", "children", "cornerRadiusToken", "keyboardFocusCornerRadius", "disabled", "ellipsisOnTextOverflow", "focused", "hovered", "icon", "iconAlignment", "id", "keyboardFocused", "klarnaLogo", "loading", "onBlur", "onClick", "onDisabledClick", "onFocus", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPress", "onPressIn", "onPressOut", "onTouchEnd", "onTouchStart", "pressed", "size", "tabIndex", "testID", "textColorToken", "textColorTokenArgs", "type"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { Loader } from '@bubble-ui/molecule-loader';
import { Text } from '@bubble-ui/primitive-text';
import { Touchable } from '@bubble-ui/primitive-touchable';
import { useTouchableControl } from '@bubble-ui/provider-touchable-control';
import { View } from '@bubble-ui/primitive-view';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { KlarnaLogoSingleLetter } from '@bubble-ui/asset-klarna-logo-single-letter';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import { useStyleSheet } from '@bubble-ui/hook-use-stylesheet';
import { useInteractiveStateCombinedWithOverrides } from '@bubble-ui/hook-use-interactive-state';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { usePropValueDeprecationWarning } from '@bubble-ui/hook-use-prop-value-deprecation-warning';
import { defaultComponentTokens } from './defaultComponentTokens';
import { getProps } from './getProps';
export function baseButton(displayName, getStyleSheet) {
  var BaseButtonComponent = _ref => {
    var {
        'aria-label': ariaLabel,
        UNSAFE_touchableProps,
        ariaLabel: ariaLabelDeprecated,
        backgroundColorToken = 'base-button/background-color',
        backgroundColorTokenArgs,
        borderColorToken = 'base-button/border-color',
        borderColorTokenArgs,
        children,
        cornerRadiusToken = 'base-button/corner-radius',
        keyboardFocusCornerRadius = 'base-button/keyboard-focus-corner-radius',
        disabled: passedDisabledProp,
        ellipsisOnTextOverflow,
        focused: passedFocusedProp,
        hovered: passedHoveredProp,
        icon: Icon,
        iconAlignment = 'left',
        id,
        keyboardFocused: passedKeyboardFocusedProp,
        klarnaLogo,
        loading,
        onBlur,
        onClick,
        onDisabledClick,
        onFocus,
        onMouseDown,
        onMouseOut,
        onMouseOver,
        onMouseUp,
        onPress,
        onPressIn,
        onPressOut,
        onTouchEnd,
        onTouchStart,
        pressed: passedPressedProp,
        size = 'regular',
        tabIndex,
        testID,
        textColorToken = 'base-button/text-color',
        textColorTokenArgs,
        type
        /**
         * @deprecated props spreading is outdated and will be removed with v7.0.0
         */
      } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);
    usePropsSpreadingDeprecationWarning({
      componentName: displayName,
      props,
      useInstead: 'UNSAFE_touchableProps',
      removalDate: 'v7.0.0'
    });
    var isIconComponent = Boolean(Icon && ! /*#__PURE__*/React.isValidElement(Icon));
    var iconComponentName = 'SomeIcon';
    usePropValueDeprecationWarning({
      componentName: displayName,
      propName: 'icon',
      propValue: "{".concat(iconComponentName, "}"),
      shouldWarn: isIconComponent,
      useInstead: "<".concat(iconComponentName, "/>"),
      removalDate: 'v8.0.0'
    });
    var designVersion = useDesignVersion();
    var designContext = useDesignContext(defaultComponentTokens);
    var {
      turnOffTouchability,
      touchablesInteractiveState
    } = useTouchableControl();
    var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombinedWithOverrides({
      disabled: passedDisabledProp,
      focused: passedFocusedProp,
      hovered: passedHoveredProp,
      keyboardFocused: passedKeyboardFocusedProp,
      onBlur,
      onFocus,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPressIn,
      onPressOut,
      onTouchEnd,
      onTouchStart,
      pressed: passedPressedProp
    }, touchablesInteractiveState);
    var {
      disabled,
      focused,
      hovered,
      keyboardFocused,
      pressed
    } = interactiveStateCombined;
    var {
      ids,
      isRegular,
      isSmall,
      isMedium,
      isIconLeftAlign
    } = getProps({
      id,
      size,
      iconAlignment
    });
    var {
      buttonStyle,
      iconWrapperStyle,
      klarnaLogoProps,
      klarnaLogoWrapperStyle,
      loaderProps,
      loadingWrapper,
      keyboardFocusStyle,
      textStyle,
      textWrapperStyle,
      viewStyle,
      iconStyle
      // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
      // Keep passing all props to the style sheet, even if we don't use them ourselves
    } = useStyleSheet(BaseButtonComponent, getStyleSheet, _objectSpread(_objectSpread(_objectSpread({}, props), eventHandlers), {}, {
      UNSAFE_touchableProps,
      ariaLabel: ariaLabel || ariaLabelDeprecated,
      backgroundColorToken,
      backgroundColorTokenArgs,
      borderColorToken,
      borderColorTokenArgs,
      children,
      cornerRadiusToken,
      designContext,
      designVersion,
      disabled,
      focused,
      hovered,
      icon: Icon,
      iconAlignment,
      id,
      ids,
      isIconLeftAlign,
      isMedium,
      isRegular,
      isSmall,
      keyboardFocusCornerRadius,
      keyboardFocused,
      klarnaLogo,
      loading,
      onClick,
      onPress,
      pressed,
      size,
      tabIndex,
      testID,
      textColorToken,
      textColorTokenArgs,
      type
    }));
    var textOverflowStyle = ellipsisOnTextOverflow ? {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    } : {};
    var buttonChildren = /*#__PURE__*/React.createElement(View, {
      style: viewStyle
    }, !loading && klarnaLogo && isRegular && /*#__PURE__*/React.createElement(View, {
      style: klarnaLogoWrapperStyle
    }, /*#__PURE__*/React.createElement(KlarnaLogoSingleLetter, klarnaLogoProps)), keyboardFocused && /*#__PURE__*/React.createElement(View, {
      style: keyboardFocusStyle
    }), loading && /*#__PURE__*/React.createElement(View, {
      id: ids.loadingWrapper,
      style: loadingWrapper
    }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
      context: designContext
    }, /*#__PURE__*/React.createElement(Loader, loaderProps))), !loading && Icon && isIconLeftAlign && /*#__PURE__*/React.createElement(View, {
      id: ids.iconWrapper,
      style: iconWrapperStyle
    }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
      context: designContext
    }, /*#__PURE__*/React.isValidElement(Icon) ? (/*#__PURE__*/React.cloneElement(Icon, iconStyle)) : /*#__PURE__*/React.createElement(Icon, iconStyle))), /*#__PURE__*/React.createElement(View, {
      style: _objectSpread(_objectSpread({}, textWrapperStyle), {}, {
        flexShrink: ellipsisOnTextOverflow ? 1 : 0
      })
    }, /*#__PURE__*/React.createElement(Text, _extends({
      id: ids.text
    }, ellipsisOnTextOverflow ? {
      numberOfLines: 1
    } : {}, {
      style: _objectSpread(_objectSpread({}, textStyle), textOverflowStyle)
    }), children)), !loading && Icon && !isIconLeftAlign && /*#__PURE__*/React.createElement(View, {
      id: ids.iconWrapper,
      style: iconWrapperStyle
    }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
      context: designContext
    }, /*#__PURE__*/React.isValidElement(Icon) ? (/*#__PURE__*/React.cloneElement(Icon, iconStyle)) : /*#__PURE__*/React.createElement(Icon, iconStyle))));
    return turnOffTouchability ? /*#__PURE__*/React.createElement(View, _extends({
      id: ids.root,
      testID: testID
    }, UNSAFE_touchableProps, props), buttonChildren) : disabled && onDisabledClick !== undefined ? /*#__PURE__*/React.createElement(Touchable, _extends({
      id: ids.root,
      testID: testID
    }, UNSAFE_touchableProps, {
      accessibilityRole: "button",
      accessibilityState: {
        disabled
      },
      "aria-label": ariaLabel || ariaLabelDeprecated,
      "aria-disabled": true,
      hitSlop: {
        bottom: 5,
        left: 5,
        right: 5,
        top: 5
      },
      onClick: onDisabledClick,
      onPress: onDisabledClick,
      style: buttonStyle,
      tabIndex: -1,
      type: type
    }, props), buttonChildren) : /*#__PURE__*/React.createElement(Touchable, _extends({
      id: ids.root,
      testID: testID
    }, UNSAFE_touchableProps, {
      accessibilityRole: "button",
      accessibilityState: {
        disabled: disabled || loading
      },
      "aria-label": ariaLabel || ariaLabelDeprecated,
      disabled: disabled || loading,
      hitSlop: {
        bottom: 5,
        left: 5,
        right: 5,
        top: 5
      },
      onClick: onClick,
      onPress: onPress,
      style: buttonStyle,
      tabIndex: tabIndex,
      type: type
    }, props, eventHandlers), buttonChildren);
  };
  BaseButtonComponent.displayName = displayName;
  return BaseButtonComponent;
}
//# sourceMappingURL=baseButton.js.map